import { useQueries, useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { fmtNum, fmtPct, TONE_BG, TONE_TEXT, trsTone } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Panel } from "@/components/common/Panel";
import { LoadingState, EmptyState } from "@/components/common/States";

const QUART_ETAT = { termine: "", en_cours: "ring-1 ring-emerald-500/50", a_venir: "opacity-50" };

const Cell = ({ q, testId }) => {
  if (!q) return <td className="px-2 py-1.5 text-center text-muted-foreground">—</td>;
  const t = trsTone(q.trs);
  return (
    <td data-testid={testId} className={cn("px-2 py-1.5 align-top", QUART_ETAT[q.statut_quart])}>
      <div className="flex items-baseline justify-between gap-2">
        <span className={cn("font-mono text-base font-bold", TONE_TEXT[t])}>{q.trs != null ? fmtNum(q.trs, 1) : "—"}</span>
        <span className="text-[10px] font-mono text-muted-foreground">{fmtNum(q.bonnes)}{q.rejets > 0 && <span className="text-amber-400"> / {fmtNum(q.rejets)}</span>}</span>
      </div>
      <div className="flex gap-0.5 mt-1">
        {[q.disponibilite, q.performance, q.qualite].map((v, i) => <div key={i} className="flex-1 h-1 bg-muted rounded-sm overflow-hidden" title={["D", "P", "Q"][i] + " " + fmtPct(v)}><div className={cn("h-full", TONE_BG[trsTone(v)])} style={{ width: `${Math.min(v || 0, 100)}%` }} /></div>)}
      </div>
    </td>
  );
};

export const MatriceLignesQuarts = ({ lignes, dateJour, refreshMs, onSelect, selected }) => {
  const quarts = useQuery({ queryKey: ["quarts"], queryFn: () => get("/quarts") });
  const qs = useQueries({ queries: lignes.map((l) => ({ queryKey: ["trs-par-quart", l.ligne_id, dateJour], queryFn: () => get("/trs/par-quart", { ligne_id: l.ligne_id, date_jour: dateJour }), refetchInterval: refreshMs })) });
  const loading = quarts.isLoading || qs.some((q) => q.isLoading);
  const qList = quarts.data || [];
  const colMoy = (qid) => { const v = qs.map((q) => q.data?.par_quart.find((p) => p.quart_id === qid)?.trs).filter((x) => x != null); return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null; };
  return (
    <Panel title="Matrice lignes × quarts" subtitle={`TRS · bonnes / rejets · barres D P Q · ${dateJour}`} testId="trs-matrice-panel" bodyClassName="p-0 overflow-auto">
      {loading ? <LoadingState rows={3} /> : lignes.length === 0 || qList.length === 0 ? <EmptyState title="Aucune ligne ou quart configuré" /> : (
        <table className="w-full text-xs border-collapse" data-testid="trs-matrice-table">
          <thead className="bg-card">
            <tr className="border-b border-border">
              <th className="px-3 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ligne</th>
              {qList.map((q) => <th key={q.id} className="px-2 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{q.nom} <span className="normal-case font-normal">{q.heure_debut}–{q.heure_fin}</span></th>)}
              <th className="px-2 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-l border-border">Journée</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((l, i) => {
              const d = qs[i].data;
              return (
                <tr key={l.ligne_id} data-testid={`trs-matrice-row-${l.ligne_id}`} onClick={() => onSelect(l.ligne_id)} className={cn("border-b border-border/50 odd:bg-muted/20 hover:bg-accent/40 cursor-pointer", selected === l.ligne_id && "bg-primary/5")}>
                  <td className={cn("px-3 py-1.5 font-semibold whitespace-nowrap", selected === l.ligne_id && "text-primary")}>{l.ligne}</td>
                  {qList.map((q) => <Cell key={q.id} q={d?.par_quart.find((p) => p.quart_id === q.id)} testId={`trs-matrice-cell-${l.ligne_id}-${q.id}`} />)}
                  <td className={cn("px-2 py-1.5 font-mono text-base font-bold border-l border-border", TONE_TEXT[trsTone(d?.journalier?.trs)])}>{d?.journalier?.trs != null ? fmtNum(d.journalier.trs, 1) : "—"}</td>
                </tr>
              );
            })}
            <tr className="bg-muted/30 border-t border-border" data-testid="trs-matrice-moyenne">
              <td className="px-3 py-1.5 eyebrow">Moyenne quart</td>
              {qList.map((q) => { const m = colMoy(q.id); return <td key={q.id} className={cn("px-2 py-1.5 font-mono font-bold", TONE_TEXT[trsTone(m)])}>{m != null ? fmtNum(m, 1) : "—"}</td>; })}
              <td className="border-l border-border" />
            </tr>
          </tbody>
        </table>
      )}
    </Panel>
  );
};
